import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { X } from 'lucide-react';
import { CS_CHAT_IMPORT_STATES, CS_CHAT_STATES } from '@cortex/domain-ac-mens-hub-api';
import { useCsChats, useCsChatDetail } from '../hooks/useCsInquiry.js';
import {
  CS_CHAT_STATE_DISPLAY,
  CsChatStateBadge,
} from '../components/cs-inquiry/CsChatStateBadge.js';
import { UnfetchedChatsBanner } from '../components/cs-inquiry/UnfetchedChatsBanner.js';

/** @graph-connects none */
const PAGE_SIZE = 50;

/**
 * @graph-connects none
 */
function formatSeconds(t: TFunction, seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return t('csChats.secondsUnit', { num: String(Math.round(seconds)) });
  if (seconds < 3600) return t('csChats.minutesUnit', { num: String(Math.round(seconds / 60)) });
  return t('csChats.hoursUnit', { num: (seconds / 3600).toFixed(1) });
}

/**
 * @graph-connects none
 */
function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

/**
 * @graph-connects none
 */
function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * @graph-connects none
 */
function defaultFrom(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * @graph-connects none
 */
function defaultTo(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * @graph-connects none
 */
function personTypeLabel(t: TFunction, personType: string): string {
  if (personType === 'user') return t('csChats.personCustomer');
  if (personType === 'manager') return t('csChats.personAgent');
  return personType;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット一覧ページ。ステータス・期間フィルタとページング付きで Channel Talk チャットを表示する。状態を取り込めていないチャットがあれば絞り込みの下に帯を出す。行クリックでチャット詳細パネルを開く。
 * @graph-connects useCsChats [calls] チャット一覧取得フック
 * @graph-connects useCsChatDetail [calls] チャット詳細取得フック
 * @graph-connects CsChatStateBadge [calls] 状態の札
 * @graph-connects UnfetchedChatsBanner [calls] 未取得の帯
 */
export function CsInquiryChatsPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [state, setState] = useState<string | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

  const { data, isLoading, error } = useCsChats({
    from,
    to,
    state,
    limit: PAGE_SIZE,
    offset,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="flex h-full">
      {/* メインコンテンツ（チャット一覧） */}
      <div
        className={`min-w-0 space-y-6 p-6 transition-all ${selectedChatId ? 'flex-1' : 'w-full'}`}
      >
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{t('csChats.title')}</h1>

        {/* フィルタ */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setOffset(0);
            }}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          />
          <span className="text-sm text-[var(--color-text-muted)]">〜</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setOffset(0);
            }}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          />
          <select
            value={state ?? ''}
            onChange={(e) => {
              setState(e.target.value || undefined);
              setOffset(0);
            }}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          >
            <option value="">{t('csChats.allStatuses')}</option>
            {CS_CHAT_STATES.map((s) => (
              <option key={s} value={s}>
                {t(CS_CHAT_STATE_DISPLAY[s].labelKey)}
              </option>
            ))}
            <optgroup label={t('csChats.importStatusGroup')}>
              {CS_CHAT_IMPORT_STATES.map((s) => (
                <option key={s} value={s}>
                  {t(CS_CHAT_STATE_DISPLAY[s].labelKey)}
                </option>
              ))}
            </optgroup>
          </select>
          {data && (
            <span className="text-sm text-[var(--color-text-muted)]">
              {t('csChats.chatCountUnit', { num: data.total.toLocaleString('ja-JP') })}
            </span>
          )}
        </div>

        <UnfetchedChatsBanner page="chats" />

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="loading-spinner" />
          </div>
        )}

        {error && (
          <Card className="p-6 text-center text-[var(--color-error)]">
            {t('common.fetchError')}
          </Card>
        )}

        {!isLoading && !error && data && data.chats.length === 0 && (
          <Card className="p-8 text-center">
            <p className="text-sm text-[var(--color-text-muted)]">{t('csChats.noChats')}</p>
          </Card>
        )}

        {!isLoading && !error && data && data.chats.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]">
                      {t('common.status')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colCreatedAt')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colClosedAt')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colMessageCount')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colFirstResponse')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colResolutionTime')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]">
                      {t('csChats.colAssignee')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.chats.map((chat) => (
                    <tr
                      key={chat.userChatId}
                      onClick={() => {
                        setSelectedChatId(
                          selectedChatId === chat.userChatId ? null : chat.userChatId,
                        );
                      }}
                      className={`cursor-pointer border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-raised)] ${
                        selectedChatId === chat.userChatId
                          ? 'bg-[var(--color-accent-primary)]/5'
                          : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <CsChatStateBadge state={chat.state} />
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                        {formatDate(chat.createdAt)}
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                        {formatDate(chat.closedAt)}
                      </td>
                      <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">
                        <span
                          title={t('csChats.messageBreakdown', {
                            customer: String(chat.customerMessages),
                            agent: String(chat.agentMessages),
                          })}
                        >
                          {chat.totalMessages}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">
                        {formatSeconds(t, chat.firstResponseSeconds)}
                      </td>
                      <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">
                        {formatSeconds(t, chat.resolutionSeconds)}
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text-secondary)]">
                        {chat.assigneeId ? chat.assigneeId.slice(0, 8) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ページング */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setOffset(Math.max(0, offset - PAGE_SIZE));
                  }}
                >
                  {t('common.prev')}
                </Button>
                <span className="text-sm text-[var(--color-text-muted)]">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => {
                    setOffset(offset + PAGE_SIZE);
                  }}
                >
                  {t('common.next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* チャット詳細パネル */}
      {selectedChatId && (
        <ChatDetailPanel
          chatId={selectedChatId}
          onClose={() => {
            setSelectedChatId(null);
          }}
        />
      )}
    </div>
  );
}

interface ChatDetailPanelProps {
  chatId: string;
  onClose: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット詳細パネル。選択されたチャットのメタ情報とメッセージのやり取りを時系列で表示する。
 * @graph-connects useCsChatDetail [calls] チャット詳細取得フック
 * @graph-connects CsChatStateBadge [calls] 状態の札
 */
function ChatDetailPanel({ chatId, onClose }: ChatDetailPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useCsChatDetail(chatId);

  return (
    <div className="w-[480px] shrink-0 border-l border-[var(--color-border)] bg-[var(--color-bg-base)]">
      {/* ヘッダ */}
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
        <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('csChats.detailTitle')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-raised)] hover:text-[var(--color-text-primary)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner" />
        </div>
      )}

      {error && (
        <div className="p-4 text-center text-sm text-[var(--color-error)]">
          {t('csChats.messageFetchError')}
        </div>
      )}

      {data && (
        <div className="flex h-[calc(100vh-120px)] flex-col">
          {/* チャットメタ情報 */}
          <div className="space-y-1 border-b border-[var(--color-border)] px-4 py-3">
            <div className="flex items-center gap-2">
              <CsChatStateBadge state={data.state} />
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('csChats.messageCount', { num: String(data.messages.length) })}
              </span>
            </div>
            <div className="flex gap-4 text-xs text-[var(--color-text-muted)]">
              <span>{t('csChats.startedAt', { time: formatDate(data.createdAt) })}</span>
              {data.closedAt && (
                <span>{t('csChats.endedAt', { time: formatDate(data.closedAt) })}</span>
              )}
            </div>
            {data.assigneeId && (
              <div className="text-xs text-[var(--color-text-muted)]">
                {t('csChats.assignedTo', { name: data.assigneeId.slice(0, 8) })}
              </div>
            )}
          </div>

          {/* メッセージ一覧 */}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {data.messages.length === 0 && (
              <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
                {t('csChats.noMessages')}
              </p>
            )}
            {data.messages.map((msg) => {
              const isAgent = msg.personType === 'manager';
              return (
                <div
                  key={msg.messageId}
                  className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'}`}
                >
                  <div className="mb-0.5 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
                    <span className="font-medium">{personTypeLabel(t, msg.personType)}</span>
                    <span>{formatTime(msg.createdAt)}</span>
                  </div>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                      isAgent
                        ? 'bg-[var(--color-accent-primary)]/10 text-[var(--color-text-primary)]'
                        : 'bg-[var(--color-bg-raised)] text-[var(--color-text-primary)]'
                    }`}
                  >
                    {msg.bodyText ? (
                      <p className="whitespace-pre-wrap break-words">{msg.bodyText}</p>
                    ) : (
                      <p className="italic text-[var(--color-text-muted)]">
                        ({t('csChats.noContent')})
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
